import { useState, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useQuery } from '@tanstack/react-query'
import {
  integrationsApi,
  type IntegrationStatus,
  type IntegrationCategory,
  type IntegrationDirection,
  type TransportProtocol,
  type ResultsDestination,
  RESULTS_DESTINATION_LABEL,
} from '@/api/integrationsApi'
import { sourcesApi, type Source } from '@/api/sourcesApi'
import { useTenant } from '@/providers/TenantProvider'
import { SourceFormModal } from '@/components/integrations/SourceFormModal'

const CATEGORIES: IntegrationCategory[] = [
  'instrument',
  'ehr',
  'lis',
  'printer',
  'immunoassay',
  'cell_counter',
  'other',
]
const PROTOCOLS: TransportProtocol[] = ['hl7', 'astm', 'fhir', 'csv', 'json', 'api', 's3']
const DIRECTIONS: IntegrationDirection[] = ['inbound', 'outbound', 'bidirectional']

type FormState = {
  name: string
  sourceId: string
  baseApiUrl: string
  apiKey: string
  status: IntegrationStatus
  category: IntegrationCategory
  protocol: TransportProtocol | ''
  direction: IntegrationDirection
  viaMiddleware: boolean
  resultsDestination: ResultsDestination
}

const EMPTY_FORM: FormState = {
  name: '',
  sourceId: '',
  baseApiUrl: 'https://api.stag.smart-cgt.com',
  apiKey: '',
  status: 'active',
  category: 'instrument',
  protocol: '',
  direction: 'inbound',
  viaMiddleware: true,
  resultsDestination: 'beaker',
}

/**
 * The integration connection form. Reused by the standalone "New integration" page
 * (create mode, no `integrationId`) and the Edit tab on the integration detail page
 * (edit mode). Owns its own load/save; the parent decides what happens after via
 * `onSaved`/`onCancel`.
 */
export function IntegrationForm({
  integrationId,
  onSaved,
  onCancel,
}: {
  integrationId?: string
  onSaved: () => void
  onCancel: () => void
}) {
  const isEdit = !!integrationId
  const { selectedTenantId } = useTenant()

  const { data: sources = [] } = useQuery<Source[]>({
    queryKey: ['sources'],
    queryFn: sourcesApi.list,
  })

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [loading, setLoading] = useState(isEdit)
  const [hasCredentials, setHasCredentials] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sourceModalOpen, setSourceModalOpen] = useState(false)

  // Load the integration in edit mode.
  useEffect(() => {
    if (!isEdit || !selectedTenantId || !integrationId) return
    setLoading(true)
    integrationsApi
      .get(selectedTenantId, integrationId)
      .then((i) => {
        setForm({
          name: i.name,
          sourceId: i.sourceId,
          baseApiUrl: (i.config?.baseApiUrl as string) ?? '',
          apiKey: '',
          status: i.status,
          category: i.category,
          protocol: i.protocol ?? '',
          direction: i.direction,
          viaMiddleware: i.viaMiddleware,
          resultsDestination: i.resultsDestination ?? 'beaker',
        })
        setHasCredentials(i.hasCredentials)
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load integration.'))
      .finally(() => setLoading(false))
  }, [isEdit, integrationId, selectedTenantId])

  // Default the source for new integrations once the catalog loads.
  useEffect(() => {
    if (!isEdit && !form.sourceId && sources.length > 0) {
      setForm((f) => ({ ...f, sourceId: sources[0].id }))
    }
  }, [isEdit, sources, form.sourceId])

  // Only an LIS integration releases results, so only it shows the destination.
  const isLis = sources.find((s) => s.id === form.sourceId)?.category === 'lis'

  const formValid = form.name.trim() && form.sourceId && form.baseApiUrl.trim() && selectedTenantId

  async function handleSave() {
    if (!selectedTenantId) return
    setSaving(true)
    setError(null)
    try {
      const config = { baseApiUrl: form.baseApiUrl }
      const secret = form.apiKey.trim() ? { apiKey: form.apiKey } : undefined
      const common = {
        name: form.name,
        config,
        secret,
        status: form.status,
        category: form.category,
        protocol: form.protocol || undefined,
        direction: form.direction,
        viaMiddleware: form.viaMiddleware,
        ...(isLis ? { resultsDestination: form.resultsDestination } : {}),
      }
      if (isEdit && integrationId) {
        await integrationsApi.update(selectedTenantId, integrationId, common)
      } else {
        await integrationsApi.create(selectedTenantId, { ...common, sourceId: form.sourceId })
      }
      onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <>
      {error && (
        <div className="text-sm text-destructive rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 mb-4">
          {error}
        </div>
      )}

      <div className="grid gap-4 max-w-3xl">
        <div className="grid gap-1.5">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            placeholder="e.g. Sysmex XP-300"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-1.5">
            <div className="flex items-center justify-between">
              <Label>Source</Label>
              {!isEdit && (
                <button
                  type="button"
                  onClick={() => setSourceModalOpen(true)}
                  className="text-xs text-primary hover:underline"
                >
                  + New source
                </button>
              )}
            </div>
            <Select
              value={form.sourceId || undefined}
              onValueChange={(v) => setForm((f) => ({ ...f, sourceId: v }))}
              disabled={isEdit}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a source type" />
              </SelectTrigger>
              <SelectContent>
                {sources.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isEdit && <p className="text-xs text-muted-foreground">Source type can't be changed.</p>}
          </div>

          <div className="grid gap-1.5">
            <Label>Category</Label>
            <Select
              value={form.category}
              onValueChange={(v) => setForm((f) => ({ ...f, category: v as IntegrationCategory }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} className="capitalize">
                    {c.replace('_', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-1.5">
            <Label>Protocol</Label>
            <Select
              value={form.protocol || undefined}
              onValueChange={(v) => setForm((f) => ({ ...f, protocol: v as TransportProtocol }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select protocol" />
              </SelectTrigger>
              <SelectContent>
                {PROTOCOLS.map((p) => (
                  <SelectItem key={p} value={p} className="uppercase">
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label>Direction</Label>
            <Select
              value={form.direction}
              onValueChange={(v) => setForm((f) => ({ ...f, direction: v as IntegrationDirection }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIRECTIONS.map((d) => (
                  <SelectItem key={d} value={d} className="capitalize">
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-start justify-between gap-4 rounded-lg border px-4 py-3">
          <div className="space-y-0.5">
            <Label htmlFor="viaMiddleware">Connect via middleware hub</Label>
            <p className="text-xs text-muted-foreground">
              {form.viaMiddleware
                ? 'Routes through the Data Innovations middleware hub.'
                : 'Connects directly to the platform, bypassing the hub.'}
            </p>
          </div>
          <Switch
            id="viaMiddleware"
            checked={form.viaMiddleware}
            onCheckedChange={(checked) => setForm((f) => ({ ...f, viaMiddleware: checked }))}
          />
        </div>

        {isLis && (
          <div className="grid gap-1.5">
            <Label htmlFor="resultsDestination">Results go to</Label>
            <Select
              value={form.resultsDestination}
              onValueChange={(v) => setForm((f) => ({ ...f, resultsDestination: v as ResultsDestination }))}
            >
              <SelectTrigger id="resultsDestination">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(RESULTS_DESTINATION_LABEL) as ResultsDestination[]).map((d) => (
                  <SelectItem key={d} value={d}>
                    {RESULTS_DESTINATION_LABEL[d]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Every outgoing result mapped under this integration is released to this Epic system.
              Nobody picks a destination at release time.
            </p>
          </div>
        )}

        <div className="grid gap-1.5">
          <Label htmlFor="baseApiUrl">Base API URL</Label>
          <Input
            id="baseApiUrl"
            placeholder="https://api.stag.smart-cgt.com"
            value={form.baseApiUrl}
            onChange={(e) => setForm((f) => ({ ...f, baseApiUrl: e.target.value }))}
          />
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="apiKey">API Key</Label>
          <Input
            id="apiKey"
            type="password"
            placeholder={
              isEdit && hasCredentials ? '•••••••• (leave blank to keep current)' : '••••••••••••'
            }
            value={form.apiKey}
            onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))}
          />
          {isEdit && (
            <p className="text-xs text-muted-foreground">
              {hasCredentials
                ? 'Credentials are set. Enter a new value only to replace them.'
                : 'No credentials stored yet.'}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Switch
            id="status"
            checked={form.status === 'active'}
            onCheckedChange={(checked) =>
              setForm((f) => ({ ...f, status: checked ? 'active' : 'inactive' }))
            }
          />
          <Label htmlFor="status">Active</Label>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 mt-8 max-w-3xl">
        <Button variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!formValid || saving}>
          {saving && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
          {isEdit ? 'Save Changes' : 'Add Integration'}
        </Button>
      </div>

      <SourceFormModal
        open={sourceModalOpen}
        onOpenChange={setSourceModalOpen}
        onSaved={(source) => setForm((f) => ({ ...f, sourceId: source.id }))}
      />
    </>
  )
}
